import { createContext, ReactNode, useContext } from "react"
import type { EpisodeOfCareNode } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { useFetchPointOfCareData } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useFetchPointOfCareData"
import type { PointOfCareState } from "@/modules/patients/detail/progress-note/point-of-care/types"
import type { Country } from "@/types/__generated__/union-types"

const PointOfCareContext = createContext<PointOfCareState | null>(null)

type PointOfCareProviderProps = { children: ReactNode; countryCode: Country; hasExternalAssistance: boolean }

export const PointOfCareProvider = ({ children, countryCode, hasExternalAssistance }: PointOfCareProviderProps) => {
  const pointOfCareData = useFetchPointOfCareData(countryCode, hasExternalAssistance)
  return <PointOfCareContext.Provider value={pointOfCareData}>{children}</PointOfCareContext.Provider>
}

export const usePointOfCareContext = (): PointOfCareState => {
  const pointOfCareState = useContext(PointOfCareContext)
  if (!pointOfCareState) throw new Error("Missing PointOfCareContext.Provider in the tree")
  return pointOfCareState
}

const ParentEpisodeOfCareContext = createContext<EpisodeOfCareNode | null>(null)

type ParentEpisodeOfCareProviderProps = { children: ReactNode; parentEpisodeOfCare: EpisodeOfCareNode }

export const ParentEpisodeOfCareProvider = ({ children, parentEpisodeOfCare }: ParentEpisodeOfCareProviderProps) => {
  return (
    <ParentEpisodeOfCareContext.Provider value={parentEpisodeOfCare}>{children}</ParentEpisodeOfCareContext.Provider>
  )
}

export const useParentEpisodeOfCareContext = (): EpisodeOfCareNode => {
  const parentEpisodeOfCare = useContext(ParentEpisodeOfCareContext)
  if (!parentEpisodeOfCare) throw new Error("Missing ParentEpisodeOfCareProvider in the tree")
  return parentEpisodeOfCare
}
